import { Component, ElementRef, HostListener, OnDestroy, OnInit, Renderer2, ViewChild } from '@angular/core';
import { AbstractWidgetComponent, AppClientConfiguration, AppClientConfigurationService, ContextChange, WidgetStyle } from '@sdbn/common';
import { interval, Subscription } from 'rxjs';
import { Transport } from '../../transport-list/model';
import { Station, YardMap } from '../yard-map.model';
import { YardMapService } from '../yard-map.service';

@Component({
  selector: 'app-yardmap',
  templateUrl: './yardmap.component.html',
  styleUrls: ['./yardmap.component.scss']
})
export class YardMapComponent extends AbstractWidgetComponent implements OnInit, OnDestroy {

  refreshEnabled = true;
  refreshInterval = 30000;
  refreshTimer: Subscription;

  urlYardviewMap: string;

  isLegendPositionRight: boolean;
  isLegendOrientationAsRow: boolean;

  planReferenceWidth = 1024;
  planReferenceHeight = 768;
  factor = 1;

  yardMap = new YardMap();

  extendedStationTile = false;

  @ViewChild('map', { static: true }) map: ElementRef;
  @ViewChild('planContainer', { static: true }) planContainer: ElementRef;


  constructor(
    private appClientConfiguration: AppClientConfiguration,
    private configurationService: AppClientConfigurationService,
    private yardMapService: YardMapService,
    private renderer: Renderer2) {
    super();
    this.loadOverview();
    this.registerRefreshTimer();
  }

  ngOnInit(): void {
    this.extendedStationTile = this.configurationService.isPropertyTrue('yardview.stations.numberplate.enabled', false);
  }

  ngOnDestroy() {

    super.ngOnDestroy();

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }
  }

  private registerRefreshTimer() {

    if (this.refreshTimer) {
      this.refreshTimer.unsubscribe();
    }

    if (this.appClientConfiguration.autoreload && this.refreshEnabled) {
      this.refreshTimer = interval(this.refreshInterval).subscribe(() => this.loadOverview());
    } else {
      console.log('Autoreload YardMapComponent disabled');
    }
  }

  private loadOverview() {
    if (this.widgetReference != null) {
      this.yardMapService.getYardMap(this.widgetReference.data.yardMap).subscribe(
        map => {
          this.yardMap = new YardMap(map);
          this.urlYardviewMap =  this.appClientConfiguration.serverURLRoot + '/client/resource/archive/' + this.yardMap.mapResourceUUID;
          this.planReferenceWidth = this.yardMap.width || 1024;
          this.planReferenceHeight = this.yardMap.height || 768;
          this.updateLegendPositionAndOrientation();
          this.updateScaleFactor();
          this.updateStationsStyle();
          setTimeout(() => {
            this.updateScaleFactor();
            this.updateStationsStyle();
          }, 200);
        }
      );
    }
  }

  onContextChange(contextChange: ContextChange) {
  }

  onSetElementAndContext() {
    this.refreshEnabled = this.widgetReference.refreshEnabled == null ? true : this.widgetReference.refreshEnabled;
    if (this.widgetReference.refreshInterval != null && this.widgetReference.refreshInterval > 0) {
      this.refreshInterval = this.widgetReference.refreshInterval * 1000;
    } else {
      this.refreshInterval = 30 * 1000;
    }
    this.registerRefreshTimer();

    this.loadOverview();
  }

  /**
   * If the image is loaded, we have to recalculate the scaling factors
   * and therefore the position of the station positions.
   *
   * NOTE: We need a small delay, so that the legend is rendered first. Otherwise the
   * planContainer has the wrong height n(full height) because of flex-grow: 1 and factor will be calculated wrong.
   */
  public mapLoaded(event) {
    setTimeout(() => this.updateScaleFactor(), 200);
  }

  /**
   * A resize must trigger a recalculation of sizes/ coordinates of stations.
   */
  @HostListener('window:resize', ['$event'])
  onResize(event) {
    this.updateScaleFactor();
    this.updateStationsStyle();
  }

  /**
   * Calculate the scale factor based on plan image and screen size.
   */
  public updateScaleFactor() {
    const img: HTMLImageElement = this.map.nativeElement;
    const containerWidth = this.planContainer.nativeElement.clientWidth;
    const containerHeight = this.planContainer.nativeElement.clientHeight;
    // calculate the factor to use. The smaller one wins and will be used for scaling
    const widthFactor = containerWidth / this.planReferenceWidth;
    const heightFactor = containerHeight / this.planReferenceHeight;

    // console.log('updateScaleFactor:', widthFactor, containerWidth, this.planReferenceWidth, heightFactor, containerHeight, this.planReferenceHeight);
    this.factor = Math.min(widthFactor, heightFactor);
    // console.log(this.factor);
    // console.log(this.planReferenceWidth * this.factor + 'px');
    // We assign hard the calculated width. This ensures, that map size and viewport of stations are in synch
    // and therefore the calculated coordinates match.
    this.renderer.setStyle(img, 'width', this.planReferenceWidth * this.factor + 'px');
    this.renderer.setStyle(img, 'height', this.planReferenceHeight * this.factor + 'px');
  }

  selectStation(station: Station) {
    if (station.transportId != null) {
      const selectedTransport = new Transport({});
      selectedTransport.id = station.transportId;
      this.dashboardContext.setContextValue('selected-transport', selectedTransport);
    } else {
      this.dashboardContext.setContextValue('selected-transport', null);
    }
  }

  private updateLegendPositionAndOrientation() {
    this.isLegendPositionRight = true;
    this.isLegendOrientationAsRow = !this.isLegendPositionRight;
  }

  private updateStationsStyle() {
    for (const station of this.yardMap.stations) {
      station.calculatedStyle = this.calculateStationStyle(station);
    }
  }

  private calculateStationStyle(station: Station) {
    return {
      left: station.xCoordinate * this.factor + 'px',
      top: station.yCoordinate * this.factor + 'px',
      padding: '5px 1px 1px 1px',
      'background-color': station.color,
      'transform-origin': 'top left',
      transform: 'scale(' + this.factor + ')'
    };
  }

  public getWaOccupancyWidth(station: Station) {
    return {
      width: Math.floor(station.usage / station.capacity * 100) + '%'
    };
  }

  getStyle(): WidgetStyle {
    return WidgetStyle.transparent;
  }

}
